/* ───────────────────────── Schematic stage geometry ─────────────────────────
   Single design-space coordinate system; the diagram is scaled to fit its
   container (see IppvCircuit). Layout reads left → right: anaesthetic machine
   (cylinders → flowmeter → vaporiser) feeds the breathing circle (APL, bag,
   soda-lime absorber) and on to the patient.                                  */
export const STAGE = { w: 900, h: 480 };

export const ZONES = [
  { x: 10, y: 16, w: 312, h: 450, label: "Anaesthetic machine" },
  { x: 334, y: 16, w: 556, h: 450, label: "Breathing circle (rebreathing)" },
];

export const NODE_POS = {
  spare:     { left: 30,  top: 46,  width: 84,  height: 150 },
  o2:        { left: 30,  top: 268, width: 84,  height: 180 },
  flowmeter: { left: 130, top: 150, width: 78,  height: 196 },
  vaporiser: { left: 214, top: 196, width: 98,  height: 118 },
  clipboard: { left: 214, top: 338, width: 98,  height: 92 },
  popoff:    { left: 350, top: 40,  width: 108, height: 92 },
  scavenging:{ left: 486, top: 44,  width: 100, height: 74 },
  bag:       { left: 350, top: 232, width: 108, height: 158 },
  sodalime:  { left: 488, top: 252, width: 86,  height: 168 },
  patient:   { left: 698, top: 168, width: 176, height: 200 },
  manometer: { left: 716, top: 30,  width: 120, height: 120 },
};

// Tubing — each segment carries a "kind" used for colour + flow direction.
export const TUBES = [
  { d: "M112 320 C 120 296 124 286 132 260", kind: "base" },   // O₂ supply → flowmeter
  { d: "M206 252 L214 256", kind: "base" },                    // flowmeter → vaporiser
  { d: "M310 256 C 342 282 372 268 402 258", kind: "fresh" },  // vaporiser → circle (fresh gas)
  { d: "M402 258 L402 150", kind: "base" },                    // manifold (vertical)
  { d: "M402 132 L402 150", kind: "base" },                    // pop-off → manifold
  { d: "M456 84 L486 82", kind: "vent" },                      // pop-off → scavenging
  { d: "M402 150 C 524 150 624 176 700 210", kind: "insp" },   // inspiratory limb → patient
  { d: "M700 318 C 620 352 560 322 530 300", kind: "exp" },    // expiratory ← patient → absorber
  { d: "M530 420 C 482 448 432 432 402 360", kind: "exp" },    // absorber → bag (return)
  { d: "M402 360 L402 300", kind: "base" },                    // bag → manifold
  { d: "M566 182 C 642 132 692 106 714 94", kind: "tap" },     // manometer pressure tap
];

export const TUBE_STYLE = {
  base:  { color: "#cbd5e1", w: 10, arrow: false, flow: false },
  fresh: { color: "#38bdf8", w: 4.5, arrow: "fresh", flow: true },
  insp:  { color: "#2563eb", w: 4.5, arrow: "insp",  flow: true },
  exp:   { color: "#14b8a6", w: 4.5, arrow: "exp",   flow: true },
  vent:  { color: "#a78bfa", w: 4,   arrow: "vent",  flow: false },
  tap:   { color: "#94a3b8", w: 3,   arrow: false,   flow: false },
};

/* ───────────────────────── Clickable equipment node ───────────────────────── */
export const Node = ({ testid, onClick, pos, children, done, danger, highlight, disabled, hint }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testid}
    title={hint || undefined}
    className={`absolute flex flex-col items-center justify-center gap-1 rounded-2xl border-2 px-2 py-2 bg-white transition-all duration-200 ${
      disabled
        ? "opacity-50 cursor-not-allowed shadow-none"
        : "cursor-pointer hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0"
    } ${
      danger
        ? "border-red-400 ring-2 ring-red-200 shadow-md"
        : done
        ? "border-emerald-300 shadow-sm"
        : highlight
        ? "border-[#0047AB] ring-4 ring-[#0047AB]/25 shadow-lg"
        : "border-slate-200 shadow-sm"
    }`}
    style={pos}
  >
    {highlight && !done && !disabled && (
      <span className="absolute -top-3 left-1/2 -translate-x-1/2 z-30 whitespace-nowrap text-[9px] font-bold bg-[#0047AB] text-white px-2 py-0.5 rounded-full shadow-md animate-pulse">
        Click here
      </span>
    )}
    {children}
  </button>
);

export const Label = ({ children }) => (
  <span className="text-[11px] font-semibold text-slate-700 leading-tight text-center">{children}</span>
);

export const Pill = ({ ok, danger, text }) => (
  <span
    className={`text-[9px] font-bold tracking-wide px-1.5 py-0.5 rounded ${
      danger ? "bg-red-100 text-red-700" : ok ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
    }`}
  >
    {text}
  </span>
);

/* ───────────────────────── Component artwork ───────────────────────── */

export const O2CylinderArt = ({ on, w = 56 }) => (
  <svg width={w} height={(w / 56) * 124} viewBox="0 0 56 124">
    <defs>
      <linearGradient id={`o2body-${on}`} x1="0" y1="0" x2="1" y2="0">
        <stop offset="0" stopColor={on ? "#166534" : "#94a3b8"} />
        <stop offset="0.45" stopColor={on ? "#22c55e" : "#cbd5e1"} />
        <stop offset="1" stopColor={on ? "#166534" : "#94a3b8"} />
      </linearGradient>
    </defs>
    <circle cx="28" cy="11" r="9" fill={on ? "#16a34a" : "#9ca3af"} stroke="#374151" strokeWidth="1.4" />
    {[0, 45, 90, 135].map((a) => (
      <line key={a}
        x1={28 + 8 * Math.cos((a * Math.PI) / 180)} y1={11 + 8 * Math.sin((a * Math.PI) / 180)}
        x2={28 - 8 * Math.cos((a * Math.PI) / 180)} y2={11 - 8 * Math.sin((a * Math.PI) / 180)}
        stroke="#374151" strokeWidth="1.3" />
    ))}
    <rect x="19" y="19" width="18" height="12" rx="2" fill="#b45309" />
    <rect x="24" y="29" width="8" height="9" fill="#6b7280" />
    <circle cx="43" cy="24" r="6" fill="#fff" stroke="#374151" strokeWidth="1" />
    <line x1="43" y1="24" x2="47" y2="20" stroke="#dc2626" strokeWidth="1.2" />
    <rect x="10" y="38" width="36" height="82" rx="13" fill={`url(#o2body-${on})`} stroke="#475569" strokeWidth="1.4" />
    <rect x="10" y="44" width="36" height="8" fill="#f8fafc" opacity="0.85" />
    <rect x="15" y="55" width="5" height="56" rx="2.5" fill="#fff" opacity="0.25" />
    <text x="28" y="88" textAnchor="middle" fontSize="13" fontWeight="800" fill={on ? "#fff" : "#64748b"}>O₂</text>
  </svg>
);

export const FlowmeterArt = ({ flow }) => {
  const on = flow > 0;
  return (
    <svg width="46" height="148" viewBox="0 0 46 148">
      <rect x="8" y="5" width="30" height="9" rx="2" fill="#475569" />
      <path d="M15 116 L13 18 L33 18 L31 116 Z" fill="#e0f2fe" stroke="#94a3b8" strokeWidth="1.5" />
      {[26, 42, 58, 74, 90, 104].map((y) => (
        <line key={y} x1="31" y1={y} x2="36" y2={y} stroke="#94a3b8" strokeWidth="1" />
      ))}
      <g style={{ transform: on ? "translateY(0px)" : "translateY(56px)", transition: "transform 0.5s ease" }}>
        <circle cx="23" cy="44" r="6" fill={on ? "#0ea5e9" : "#475569"} stroke="#0c4a6e" strokeWidth="1" />
        <ellipse cx="21" cy="42" rx="2" ry="1.4" fill="#fff" opacity="0.7" />
      </g>
      <rect x="6" y="118" width="34" height="22" rx="4" fill="#334155" />
      {[12, 18, 24, 30].map((x) => (
        <line key={x} x1={x} y1="120" x2={x} y2="138" stroke="#1e293b" strokeWidth="1.4" />
      ))}
    </svg>
  );
};

export const VaporiserArt = ({ pct, off, backOn }) => {
  const shown = off && !backOn ? 0 : pct;
  const a = ((-135 + (shown / 5) * 270 - 90) * Math.PI) / 180;
  const px = 48 + 18 * Math.cos(a);
  const py = 56 + 18 * Math.sin(a);
  return (
    <svg width="92" height="86" viewBox="0 0 96 92">
      <defs>
        <linearGradient id="vapbody" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#f1f5f9" /><stop offset="1" stopColor="#cbd5e1" />
        </linearGradient>
      </defs>
      <rect x="6" y="22" width="84" height="64" rx="6" fill="url(#vapbody)" stroke="#94a3b8" strokeWidth="1.5" />
      <rect x="6" y="12" width="84" height="14" rx="5" fill="#7c3aed" />
      <rect x="70" y="14" width="14" height="10" rx="2" fill="#5b21b6" />
      <circle cx="48" cy="56" r="24" fill="#fff" stroke="#334155" strokeWidth="2.5" />
      {[0, 1, 2, 3, 4, 5].map((n) => {
        const ta = ((-135 + (n / 5) * 270 - 90) * Math.PI) / 180;
        return (
          <line key={n}
            x1={48 + 20 * Math.cos(ta)} y1={56 + 20 * Math.sin(ta)}
            x2={48 + 24 * Math.cos(ta)} y2={56 + 24 * Math.sin(ta)}
            stroke="#475569" strokeWidth="1.5" />
        );
      })}
      <line x1="48" y1="56" x2={px} y2={py} stroke={shown > 0 ? "#7c3aed" : "#94a3b8"} strokeWidth="3" strokeLinecap="round" style={{ transition: "all 0.4s ease" }} />
      <circle cx="48" cy="56" r="3.5" fill="#1e293b" />
    </svg>
  );
};

const GRANULES = [];
for (let r = 0; r < 9; r++) {
  for (let c = 0; c < 4; c++) {
    GRANULES.push({ x: 17 + c * 7 + (r % 2 ? 3 : 0), y: 27 + r * 10, r: 2.6 + ((r + c) % 2) });
  }
}
export const SodaLimeArt = ({ checked, exhausted }) => (
  <svg width="52" height="140" viewBox="0 0 56 140">
    <defs><clipPath id="slclip"><rect x="13" y="21" width="30" height="98" rx="3" /></clipPath></defs>
    <rect x="8" y="7" width="40" height="13" rx="4" fill="#475569" />
    <rect x="8" y="119" width="40" height="13" rx="4" fill="#475569" />
    <rect x="12" y="20" width="32" height="100" rx="3" fill="#f8fafc" stroke="#94a3b8" strokeWidth="1.5" />
    <g clipPath="url(#slclip)">
      {GRANULES.map((g, i) => (
        <circle key={`${g.x}-${g.y}`} cx={g.x} cy={g.y} r={g.r}
          fill={exhausted ? "#8b5cf6" : checked ? (i % 4 === 0 ? "#c4b5fd" : "#ffffff") : "#cbd5e1"}
          stroke="#94a3b8" strokeWidth="0.5" />
      ))}
    </g>
    <rect x="13" y="20" width="6" height="99" rx="3" fill="#fff" opacity="0.35" />
    {checked && !exhausted && (
      <g><circle cx="40" cy="30" r="7" fill="#10b981" /><path d="M37 30 l2.2 2.2 l4 -4.4" stroke="#fff" strokeWidth="1.8" fill="none" strokeLinecap="round" /></g>
    )}
  </svg>
);

export const PopOffArt = ({ closed }) => (
  <svg width="60" height="58" viewBox="0 0 62 60">
    <rect x="18" y="34" width="26" height="20" rx="3" fill="#94a3b8" stroke="#64748b" strokeWidth="1.4" />
    <rect x="44" y="38" width="15" height="9" rx="2" fill="#64748b" />
    <rect x="3" y="38" width="15" height="9" rx="2" fill="#64748b" />
    <rect x="27" y={closed ? 28 : 22} width="8" height="12" fill="#cbd5e1" />
    <g style={{ transition: "transform 0.35s ease" }} transform={`translate(0 ${closed ? 7 : 0})`}>
      <circle cx="31" cy="18" r="14" fill={closed ? "#ef4444" : "#22c55e"} stroke="#1f2937" strokeWidth="1.4" />
      {Array.from({ length: 12 }).map((_, i) => {
        const aa = (i * 30 * Math.PI) / 180;
        return <line key={`knurl-${i}`} x1={31 + 11 * Math.cos(aa)} y1={18 + 11 * Math.sin(aa)} x2={31 + 14 * Math.cos(aa)} y2={18 + 14 * Math.sin(aa)} stroke="#1f2937" strokeWidth="1.3" />;
      })}
      <circle cx="31" cy="18" r="4" fill="#1f2937" />
    </g>
  </svg>
);

export const ReservoirBagArt = ({ filled, squeezed, overfilled }) => (
  <svg width="64" height="104" viewBox="0 0 72 118">
    <defs>
      <radialGradient id="bagg" cx="0.4" cy="0.32" r="0.75">
        <stop offset="0" stopColor={filled ? "#6b7280" : "#9ca3af"} /><stop offset="1" stopColor="#111827" />
      </radialGradient>
    </defs>
    <rect x="31" y="2" width="10" height="20" rx="2" fill="#1f2937" />
    <g className={overfilled ? "alarm-pulse" : undefined}
      style={{ transform: squeezed ? "scale(0.68)" : overfilled ? "scale(1.2)" : "scale(1)", transformOrigin: "center", transformBox: "fill-box", transition: "transform 0.35s ease" }}>
      <ellipse cx="36" cy="70" rx="31" ry="46" fill="url(#bagg)" stroke={overfilled ? "#ef4444" : "#0b0f17"} strokeWidth={overfilled ? 3 : 1.5} />
      <ellipse cx="27" cy="52" rx="6" ry="13" fill="#e5e7eb" opacity="0.3" />
    </g>
  </svg>
);

export const ScavengingArt = ({ on }) => (
  <svg width="58" height="44" viewBox="0 0 58 44">
    <rect x="6" y="6" width="46" height="20" rx="4" fill={on ? "#7c3aed" : "#cbd5e1"} stroke="#64748b" strokeWidth="1.3" />
    <path d="M14 26 L29 40 L44 26 Z" fill={on ? "#a78bfa" : "#e2e8f0"} stroke="#64748b" strokeWidth="1.2" />
    <text x="29" y="20" textAnchor="middle" fontSize="9" fontWeight="800" fill={on ? "#fff" : "#64748b"}>AGSS</text>
    {on && <path d="M29 30 l0 8 M25 34 l4 4 l4 -4" stroke="#fff" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />}
  </svg>
);

export const ClipboardArt = ({ signed }) => (
  <svg width="50" height="58" viewBox="0 0 50 58">
    <rect x="6" y="6" width="38" height="48" rx="4" fill="#f8fafc" stroke="#94a3b8" strokeWidth="1.5" />
    <rect x="17" y="2" width="16" height="8" rx="2" fill="#64748b" />
    {[18, 26, 34].map((y) => <line key={y} x1="13" y1={y} x2="37" y2={y} stroke="#cbd5e1" strokeWidth="2" strokeLinecap="round" />)}
    {signed ? (
      <g><path d="M14 44 c 6 -8 12 -8 22 -2" stroke="#0047AB" strokeWidth="2" fill="none" strokeLinecap="round" />
        <circle cx="38" cy="14" r="7" fill="#10b981" /><path d="M35 14 l2.2 2.2 l4 -4.4" stroke="#fff" strokeWidth="1.8" fill="none" strokeLinecap="round" /></g>
    ) : (
      <line x1="13" y1="44" x2="37" y2="44" stroke="#e2e8f0" strokeWidth="2" strokeLinecap="round" />
    )}
  </svg>
);

export const manoPoint = (v, R) => {
  const a = ((-120 + (v / 60) * 240 - 90) * Math.PI) / 180;
  return [60 + R * Math.cos(a), 60 + R * Math.sin(a)];
};
export const manoArc = (v1, v2, R) => {
  const [x1, y1] = manoPoint(v1, R);
  const [x2, y2] = manoPoint(v2, R);
  return `M${x1} ${y1} A ${R} ${R} 0 0 1 ${x2} ${y2}`;
};
